import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { tint } from '../../../helpers';

type CalWeekHeaderStyleProps = { fontWeight?: string | false };

type CalContainerStyleProps = { btnDiameter: number };

export const calWeekHeaderStyle = (props?: CalWeekHeaderStyleProps) => css`
  color: ${theme.colors.neutral80};
  font-size: ${theme.fontSize(16)};
  margin-bottom: ${theme.spacing(1)};
  ${props?.fontWeight && `font-weight: ${theme.font.weight[props?.fontWeight]};`}
`;

export const calContainerStyle = ({ btnDiameter }: CalContainerStyleProps) => {
  const circleDiameter = theme.spacing(btnDiameter);
  const inRangeBackground = theme.colors.neutral10 ?? tint(0.85, theme.colors.primary50);
  const spaceBetweenButtons = theme.spacing(1);
  return css`
    width: ${7 * (btnDiameter * 8 + 8)}px;

    .calendar__months__years {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .cal__month-name {
      margin: 0;
    }

    .cal__day {
      height: ${circleDiameter};
      width: ${circleDiameter};
      border-radius: 50%;
      margin: calc(${spaceBetweenButtons} / 2);
    }

    button.cal__day {
      position: relative;

      ::after {
        content: '';
        width: 100%;
        height: 100%;
        display: block;
        border: 2px solid ${theme.colors.primary50};
        border-radius: 50%;
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        transition: opacity 150ms ease-out;
      }

      &[aria-disabled='true'] {
        color: ${theme.colors.neutral60};
        cursor: default;

        ::after {
          border-color: ${theme.colors.neutral5};
        }
      }

      :focus {
        outline: none;
      }

      :focus:not(:active)::after {
        opacity: 1;
      }

      :hover:not([aria-disabled='true']) {
        /*Make sure our range-start and range-end don't override */
        background-color: ${theme.colors.neutral5} !important;
      }

      &[in-range='true']:hover {
        /*Make sure our range-start and range-end don't override */
        background-color: ${inRangeBackground} !important;
      }

      /*Selected day styles */
      &[aria-selected='true'] {
        color: ${theme.colors.white};
        background-color: ${theme.colors.primary50};

        :hover,
        :focus {
          /*Make sure our range-start and range-end don't override */
          background-color: ${theme.colors.neutral10} !important;
          ${theme.colors.neutral90 && `color: ${theme.colors.neutral90} !important;`}
        }
      }

      &[data-is-today='true']:not([aria-selected='true']) {
        color: ${theme.colors.primary50};
        font-weight: ${theme.font.weight.bold};

        &[aria-disabled='true'] {
          color: ${tint(0.5, theme.colors.primary50)};
        }
      }

      &[in-range='true'] {
        :not(:first-of-type):not(:last-of-type):not([range-start='true']):not([range-end='true'])::before {
          content: '';
          position: absolute;
          height: ${circleDiameter};
          width: calc(${circleDiameter} + ${spaceBetweenButtons});
          top: 0;
          left: 0;
          z-index: ${theme.zIndex.background};
          background-color: ${inRangeBackground};
        }

        &[range-start='true']:not([aria-selected='true']),
        &[range-end='true']:not([aria-selected='true']) {
          background-color: ${inRangeBackground};
          border-radius: 50%;
        }

        :first-of-type:not(:last-of-type):not([range-start='true']):not([range-end='true']) {
          background-color: ${inRangeBackground};
          border-radius: 50%;

          ::before {
            width: calc(${circleDiameter} / 2 + ${spaceBetweenButtons});
            left: 50%;
            content: '';
            position: absolute;
            height: ${circleDiameter};
            top: 0;
            z-index: ${theme.zIndex.background};
            background-color: ${inRangeBackground};
          }
        }

        &[range-start='true']:not(:last-of-type)::before {
          width: calc(${circleDiameter} / 2 + ${spaceBetweenButtons});
          left: 50%;
          content: '';
          position: absolute;
          height: ${circleDiameter};
          top: 0;
          z-index: ${theme.zIndex.background};
          background-color: ${inRangeBackground};
        }

        :last-of-type:not([range-start='true']):not([range-end='true']) {
          background-color: ${inRangeBackground};
          border-radius: 50%;

          :not(:first-of-type)::before {
            width: calc(${circleDiameter} / 2 + ${spaceBetweenButtons});
            right: 50%;
            content: '';
            position: absolute;
            height: ${circleDiameter};
            top: 0;
            z-index: ${theme.zIndex.background};
            background-color: ${inRangeBackground};
          }
        }

        &[range-end='true']:not(:first-of-type)::before {
          width: calc(${circleDiameter} / 2 + ${spaceBetweenButtons});
          right: 50%;
          content: '';
          position: absolute;
          height: ${circleDiameter};
          top: 0;
          z-index: ${theme.zIndex.background};
          background-color: ${inRangeBackground};
        }
      }
    }

    button.cal__months {
      text-align: center;
      flex: 0 0 30%;
      padding: ${theme.spacing(1.5)} 0;
      border-radius: ${theme.borderRadius.small};
      outline: none;
      position: relative;

      ::after {
        content: '';
        width: calc(100% + ${theme.spacing(1.5)});
        height: calc(100% + ${theme.spacing(1.5)});
        display: block;
        border: 2px solid ${theme.colors.primary50};
        border-radius: ${theme.borderRadius.small};
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        transition: opacity 150ms ease-out;
      }

      &:not(:nth-last-of-type(-n + 3)) {
        margin-bottom: ${theme.spacing(1.5)};
      }

      &[data-is-today='true']:not([aria-selected='true']) {
        color: ${theme.colors.primary50};
        font-weight: ${theme.font.weight.bold};

        &[aria-disabled='true'] {
          color: ${tint(0.5, theme.colors.primary50)};
        }
      }

      &[aria-disabled='true'] {
        color: ${theme.colors.neutral60};
        cursor: default;

        ::after {
          border-color: ${theme.colors.neutral5};
        }
      }

      &[aria-selected='true'] {
        color: ${theme.colors.white};
        background-color: ${theme.colors.primary50};

        :hover,
        :focus {
          background-color: ${theme.colors.neutral10};
          ${theme.colors.neutral90 && `color: ${theme.colors.neutral90};`}
        }
      }

      :hover:not([aria-disabled='true']) {
        background-color: ${theme.colors.neutral5};
        transition: background-color 0.2s ease;
      }

      &:focus {
        transition: fill 250ms ease-in-out;
        ::after {
          opacity: 1;
        }
      }
    }
    .calendar__single {
      & button {
        padding: ${theme.spacing(2.5)} 0;
      }
    }
  `;
};

export const CalendarCommonStyles = {
  calWeekHeaderStyles: (props?: CalWeekHeaderStyleProps) => calWeekHeaderStyle(props),
  calContainerStyles: (props?: CalContainerStyleProps) =>
    calContainerStyle({
      btnDiameter: props?.btnDiameter || 4,
    }),
};
